import { createContext, useContext, useState } from 'react'
import rest from '../rest'
import {
  Pauta,
  PautaContextData,
  PautasProviderProps,
} from './types/pautasTypes'
import dayjs from 'dayjs'

export const PautasContext = createContext<PautaContextData>(
  {} as PautaContextData
)

export function PautasProvider({ children }: PautasProviderProps) {
  const initialStore: Pauta = {
    data: dayjs(new Date()).format('YYYY-MM-DD'),
    publicada: false,
    titulo: '',
    descricao: '',
    responsavel: undefined,
    area_relatora: undefined,
    tipopauta: undefined,
    departamento: undefined,
  }
  const [store, _setStore] = useState(initialStore)
  const [pautas, setPautas] = useState<Pauta[]>([])

  const [error, setError] = useState<{ [index: string]: any }>({})
  const [success, setSuccess] = useState('')
  const [loading, setLoading] = useState('')

  const setStore = (obj: any) => {
    const newStore = { ...store, ...obj }
    _setStore(newStore)
  }

  const checkLoading = (action: string) => loading === action

  const clearError = () => {
    setError({})
    setSuccess('')
  }

  const checkError = (field: string) => {
    if (error[field]) {
      const fieldError = error[field]
      if (Array.isArray(fieldError)) {
        return fieldError[0]
      } else {
        return fieldError
      }
    } else {
      return null
    }
  }

  const get = async (id: string) => {
    try {
      clearError()
      setLoading('get')
      const json: Pauta = await rest(`pauta/${id}/`)
      _setStore(json)
    } catch (e) {
      setError(e)
    } finally {
      setLoading('')
    }
  }

  const save = async (loadingName = 'save', successText = 'Salvo com sucesso', publicada = false) => {
    try {
      clearError()
      setLoading(loadingName)
      const newStore = { ...store, publicada }
      const json: Pauta = store.id
        ? await rest(`pauta/${store.id}/`, 'PUT', newStore)
        : await rest(`pauta/`, 'POST', newStore)
      _setStore(json)
      setSuccess(successText)
    } catch (e) {
      setError(e)
    } finally {
      setLoading('')
    }
  }

  const publicar = async () => {
    await save('publicar', 'Salva e publicada com sucesso', true)
  }

  const removerPublicacao = async () => {
    await save('removerPublicacao', 'Publicação removida com sucesso')
  }

  const list = async () => {
    try {
      clearError()
      setLoading('list')
      const json: Pauta[] = await rest('pauta/')
      setPautas(json)
    } catch (e) {
      setError(e)
    } finally {
      setLoading('')
    }
  }

  const excluir = async (id?: number) => {
    try {
      clearError()
      setLoading('excluir')
      const pautaId = id || store.id
      await rest(`pauta/${pautaId}/`, 'DELETE')
      _setStore(initialStore)
      setSuccess('Excluído com sucesso')
    } catch (e) {
      setError(e)
    }
    setLoading('')
  }

  const api = {
    setStore,
    checkError,
    checkLoading,
    get,
    save,
    publicar,
    removerPublicacao,
    list,
    excluir
  }

  return (
    <PautasContext.Provider
      value={{
        store,
        pautas,
        success,
        api
      }}
    >
      {children}
    </PautasContext.Provider>
  )
}

export function usePautas() {
  const context = useContext(PautasContext)
  return context
}
